import React, { useEffect } from 'react'
import { theme } from '@notino/react-styleguide'
import styled, { StyleSheetManager, ThemeProvider } from 'styled-components'
import { StyledTarget } from 'styled-components/dist/types'
import isPropValid from '@emotion/is-prop-valid'

import dayjs from 'dayjs'
import localeData from 'dayjs/plugin/localeData'
import weekday from 'dayjs/plugin/weekday'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isBetween from 'dayjs/plugin/isBetween'
import LocalizedFormat from 'dayjs/plugin/localizedFormat'
import utc from 'dayjs/plugin/utc'
import 'dayjs/locale/sk'
import 'dayjs/locale/cs'
import 'dayjs/locale/hu'
import 'dayjs/locale/ro'
import 'dayjs/locale/bg'
import 'dayjs/locale/de-at'
import 'dayjs/locale/hr'

import Routes from './routes/routes'

// styles
import '@notino/react-styleguide/dist/styleguide.css'
import GlobalStyles, { AppContainer } from './styles/globalStyles'
import 'overlayscrollbars/styles/overlayscrollbars.css'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'

// types
import { ContextProps } from './types/types'

// utils
import LanguageProvider from './utils/languageProvider'
import { DEFAULT_LOCALE, shopsConfig } from './appDefaults'
import { APP_CONTAINER_ID } from './utils/enums'
import MyLocationProvider from './utils/myLocationProvider'
import { ModalOpenProvider } from './atoms/Modal/ModalOpenContext'

// components
import ToastContainer from './components/ToastContainer/ToastContainer'

dayjs.locale(DEFAULT_LOCALE.toLowerCase())
dayjs.extend(localeData)
dayjs.extend(weekday)
dayjs.extend(LocalizedFormat)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)
dayjs.extend(isBetween)
dayjs.extend(utc)

const AppVersion = styled.div`
	display: none;
`

const shouldForwardProp = (propName: string, target: StyledTarget<'web'>) => {
	if (typeof target === 'string') {
		return isPropValid(propName)
	}
	return true
}

const AppLayout: React.FC<React.PropsWithChildren<ContextProps>> = (props) => {
	useEffect(() => {
		const dayjsLocale = (shopsConfig.find((config) => config.lang === props.locale)?.locale || DEFAULT_LOCALE).toLowerCase()
		dayjs.locale(dayjsLocale)
	}, [props.locale])

	return (
		<StyleSheetManager shouldForwardProp={shouldForwardProp}>
			<ThemeProvider theme={theme}>
				<GlobalStyles />
				<LanguageProvider {...props}>
					<ModalOpenProvider>
						<MyLocationProvider>
							<AppContainer id={APP_CONTAINER_ID}>
								<ToastContainer />
								<Routes {...props} />
							</AppContainer>
						</MyLocationProvider>
					</ModalOpenProvider>
				</LanguageProvider>
			</ThemeProvider>
			<AppVersion id='app_version'>{props.appVersion}</AppVersion>
		</StyleSheetManager>
	)
}

export default AppLayout
